import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/services/api/api.service';
import { environment } from '@environment/environment';
import { map, Observable } from 'rxjs';
import {
  JobDefinitionsResponse,
  MaxxBrandKit,
  MaxxBrandKitUpdate,
  MaxxMemoriesResponse,
  MaxxMemory,
  MaxxMonitor,
  MaxxMonitorInput,
  MaxxMonitorUpdate,
  MaxxSlackChannel,
  MaxxSlackSettingsResponse,
} from './maxx-settings.model';

const MONITOR_AGENT_NAME = 'EXTERNAL_ANALYST_AGENT';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export function maxxSettingsErrorMessage(error: unknown): string {
  if (!(error instanceof HttpErrorResponse)) return DEFAULT_ERROR_MESSAGE;
  const message = error.error?.message;
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && message.length) return message.join('. ');
  return DEFAULT_ERROR_MESSAGE;
}

@Injectable({ providedIn: 'root' })
export class MaxxSettingsService {
  private readonly baseUrl = `${environment.MAXX_API_URL}/maxx-settings`;
  private readonly jobDefinitionsUrl = `${environment.MAXX_API_URL}/job-definitions`;

  constructor(
    private readonly http: HttpClient,
    private readonly apiService: ApiService,
  ) {}

  getSlackChannel(): Observable<MaxxSlackChannel | null> {
    return this.http
      .get<MaxxSlackSettingsResponse>(`${this.baseUrl}/slack`, { headers: this.buildHeaders() })
      .pipe(map((response) => response.channel));
  }

  checkSlackChannel(channelId: string): Observable<MaxxSlackChannel> {
    return this.http
      .post<MaxxSlackSettingsResponse>(`${this.baseUrl}/slack/check`, { channelId }, { headers: this.buildHeaders() })
      .pipe(map((response) => response.channel));
  }

  connectSlackChannel(channelId: string): Observable<MaxxSlackChannel> {
    return this.http
      .put<MaxxSlackSettingsResponse>(`${this.baseUrl}/slack`, { channelId }, { headers: this.buildHeaders() })
      .pipe(map((response) => response.channel));
  }

  disconnectSlackChannel(): Observable<void> {
    return this.http
      .delete<MaxxSlackSettingsResponse>(`${this.baseUrl}/slack`, { headers: this.buildHeaders() })
      .pipe(map(() => undefined));
  }

  getMemories(): Observable<MaxxMemory[]> {
    return this.http
      .get<MaxxMemoriesResponse>(`${this.baseUrl}/memories`, { headers: this.buildHeaders() })
      .pipe(map((response) => response.memories));
  }

  deleteMemories(recordIds: string[]): Observable<MaxxMemory[]> {
    return this.http
      .post<MaxxMemoriesResponse>(`${this.baseUrl}/memories/delete`, { recordIds }, { headers: this.buildHeaders() })
      .pipe(map((response) => response.memories));
  }

  getBrandKit(): Observable<MaxxBrandKit> {
    return this.http.get<MaxxBrandKit>(`${this.baseUrl}/brand-kit`, { headers: this.buildHeaders() });
  }

  saveBrandKit(update: MaxxBrandKitUpdate): Observable<MaxxBrandKit> {
    const form = new FormData();
    form.append('enabled', String(update.enabled));
    form.append('brandName', update.brandName);
    form.append('primaryColor', update.primaryColor);
    form.append('inkColor', update.inkColor);
    if (update.logo) form.append('logo', update.logo);
    if (update.logoOnDark) form.append('logoOnDark', update.logoOnDark);
    if (update.removeLogoOnDark) form.append('removeLogoOnDark', 'true');
    return this.http.put<MaxxBrandKit>(`${this.baseUrl}/brand-kit`, form, { headers: this.buildHeaders() });
  }

  getMonitors(): Observable<MaxxMonitor[]> {
    return this.http
      .get<JobDefinitionsResponse<MaxxMonitor[]>>(this.jobDefinitionsUrl, {
        headers: this.buildHeaders(),
        params: { type: 'alert' },
      })
      .pipe(map((response) => response.data));
  }

  createMonitor(input: MaxxMonitorInput): Observable<MaxxMonitor> {
    const body = { ...input, type: 'alert', agentName: MONITOR_AGENT_NAME };
    return this.http
      .post<JobDefinitionsResponse<MaxxMonitor>>(this.jobDefinitionsUrl, body, { headers: this.buildHeaders() })
      .pipe(map((response) => response.data));
  }

  updateMonitor(id: string, changes: MaxxMonitorUpdate): Observable<MaxxMonitor> {
    return this.http
      .patch<JobDefinitionsResponse<MaxxMonitor>>(`${this.jobDefinitionsUrl}/${id}`, changes, {
        headers: this.buildHeaders(),
      })
      .pipe(map((response) => response.data));
  }

  deleteMonitor(id: string): Observable<void> {
    return this.http
      .delete(`${this.jobDefinitionsUrl}/${id}`, { headers: this.buildHeaders() })
      .pipe(map(() => undefined));
  }

  private buildHeaders(): Record<string, string> {
    return {
      Authorization: `Bearer ${this.apiService.jwtToken}`,
      'x-tenant-id': String(this.apiService.shopId),
    };
  }
}
